<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue'
  import type { DateRange } from 'radix-vue'
  import {
    MoreVertical,
    Calendar,
    Search,
    Trash2,
    Plus,
    FileDown,
    Eye,
    Clock4,
    StickyNote,
    X,
    Columns3,
    List,
    Undo2,
    MessageCircleMore,
    ChevronDown
  } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import NewPageBanner from '@/components/ui/new-page-banner/NewPageBanner.vue'

  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'
  import { Badge } from '@/components/ui/badge'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import { TableCell, TableRow } from '@/components/ui/table'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { RangeCalendar } from '@/components/ui/range-calendar'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import {
    CasesService,
    StateEnum,
    DocumentStatusEnum,
    type Document,
    type CustomerCase,
    FeatureEnum
  } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'
  import { navigateWithModifiers } from '@/composables/useNavigateWithModifiers'
  import CaseStatusSelect from '@/components/app/cases/CaseStatusSelect.vue'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import KanbanView from '@/components/app/cases/CaseKanbanView.vue'
  import { useRouter } from 'vue-router'
  import { genericErrorToast } from '@/lib/toasts'
  import { useUserStore } from '@/store/admin/user'
  import type { CalendarDate, DateValue } from '@internationalized/date'
  import { TooltipProvider, TooltipTrigger, TooltipContent, Tooltip } from '@/components/ui/tooltip'
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import DocumentStatusBadge from '@/components/app/cases/documents/DocumentStatusBadge.vue'
  import FeedbackModal from '@/components/app/cases/feedbackModal.vue'

  const router = useRouter()
  const { translateLawField } = useLawFieldTranslation()
  const { translateLawTopic } = useLawTopicTranslation()

  const tableRef = ref()
  const selectedLegalField = ref('')
  const selectedCaseWorker = ref('')
  const selectedStatus = ref('')
  const selectedSortOption = ref('modified_at')
  const searchQuery = ref('')
  const viewType = ref('list') // 'list' or 'grid'
  const lawFieldOptions = computed(() => {
    if (!user.value?.org.law_fields) return []
    return (user.value?.org.law_fields as Array<string>).map((value) => ({
      value,
      title: translateLawField(value)
    }))
  })
  const caseWorkerOptions = computed(() => {
    if (!user.value?.org.users) return []
    return (user.value?.org.users as Array<any>).map((user) => ({
      value: user.uuid,
      title: `${user.first_name} ${user.last_name}`
    }))
  })
  const cases = ref<CustomerCase[]>([])
  const loading = ref(false)

  // Initialize dateRange with empty values
  const dateRange = ref<DateRange>({
    start: undefined,
    end: undefined
  })

  const userStore = useUserStore()
  const user = computed(() => userStore.user)

  const formatDateForDisplay = (date: CalendarDate | undefined) => {
    if (!date) return ''
    const jsDate = new Date(date.year, date.month - 1, date.day)
    return jsDate.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const dateRangeText = computed(() => {
    if (!dateRange.value.start && !dateRange.value.end) {
      return t('timeframe')
    }
    const startDate = formatDateForDisplay(dateRange.value.start as CalendarDate)
    const endDate = formatDateForDisplay(dateRange.value.end as CalendarDate)
    if (
      dateRange.value.start &&
      dateRange.value.end &&
      dateRange.value.start.year === dateRange.value.end.year &&
      dateRange.value.start.month === dateRange.value.end.month &&
      dateRange.value.start.day === dateRange.value.end.day
    ) {
      return startDate
    }
    return `${startDate} - ${endDate}`
  })

  const { t } = useTranslation({
    en: {
      title: 'Inquiries',
      newInquiry: 'New Inquiry',
      legalField: 'Legal Field',
      caseWorker: 'Case Worker',
      status: 'Status',
      timeframe: 'Timeframe',
      name: 'Name',
      legalFieldTheme: 'Legal Field/Theme',
      document: 'Document',
      documents: 'Documents',
      noDocuments: '0 Documents',
      lastUpdated: 'Last updated',
      lastAdded: 'Last added',
      createdAt: 'Created at',
      updatedAt: 'Updated at',
      updated: 'Updated',
      created: 'Created',
      notAssigned: 'Not assigned',
      newInquiryStatus: 'New Inquiry',
      inProgress: 'In Progress',
      rejected: 'Rejected',
      completed: 'Completed',
      showMore: 'Show more',
      delete: 'Delete',
      deleteConfirmation: 'Are you sure you want to delete this case?',
      cancel: 'Cancel',
      allLegalFields: 'All Legal Fields',
      allCaseWorkers: 'All Case Workers',
      allStatuses: 'All Status',
      view: 'View',
      notes: 'Notes',
      viewAllDocuments: 'View All Documents',
      new: 'NEW',
      backToOldView: 'Switch back to old view',
      newView: 'New view',
      giveFeedback: 'Give Feedback'
    },
    de: {
      title: 'Anfragen',
      newInquiry: 'Neue Anfrage anlegen',
      legalField: 'Rechtsgebiet',
      caseWorker: 'Sachbearbeiter',
      status: 'Status',
      timeframe: 'Zeitraum',
      name: 'Name',
      legalFieldTheme: 'Rechtsgebiet/Thema',
      document: 'Dokument',
      documents: 'Dokumente',
      noDocuments: '0 Dokumente',
      lastUpdated: 'Zuletzt aktualisiert',
      lastAdded: 'Zuletzt eingegangen',
      createdAt: 'Erstellt am',
      updatedAt: 'Aktualisiert am',
      updated: 'Aktualisiert',
      created: 'Erstellt',
      notAssigned: 'Nicht zugewiesen',
      newInquiryStatus: 'Neue Anfrage',
      inProgress: 'In Bearbeitung',
      rejected: 'Abgelehnt',
      completed: 'Abgeschlossen',
      showMore: 'Mehr anzeigen',
      delete: 'Löschen',
      deleteConfirmation: 'Sind Sie sicher, dass Sie diesen Fall löschen möchten?',
      cancel: 'Abbrechen',
      allLegalFields: 'Alle Rechtsgebiete',
      allCaseWorkers: 'Alle Sachbearbeiter',
      allStatuses: 'Alle Status',
      view: 'Ansehen',
      notes: 'Notizen',
      viewAllDocuments: 'Alle Dokumente anzeigen',
      new: 'NEU',
      backToOldView: 'Zurück zur alten Ansicht',
      newView: 'Neues Design',
      giveFeedback: 'Feedback geben'
    }
  })

  const statusOptions = [
    { value: StateEnum.NEW, title: t('newInquiryStatus') },
    { value: StateEnum.IN_PROGRESS, title: t('inProgress') },
    { value: StateEnum.REJECTED, title: t('rejected') },
    { value: StateEnum.COMPLETED, title: t('completed') }
  ]

  const sortOptions = [
    { value: 'modified_at', title: t('lastUpdated') },
    { value: 'created_at', title: t('lastAdded') }
  ]

  const formatDateForAPI = (dateObj: any) => {
    if (!dateObj) return undefined
    return `${dateObj.year}-${String(dateObj.month).padStart(2, '0')}-${String(dateObj.day).padStart(2, '0')}`
  }

  const fetchCases = async (options?: FetchDataOptions): Promise<PaginatedResponse<any>> => {
    loading.value = true
    try {
      // Add ordering parameter to the API call
      const queryParams: any = {
        limit: options?.limit || 10,
        offset: options?.offset || 0,
        clerk: selectedCaseWorker.value || undefined,
        search: options?.search,
        legalField: selectedLegalField.value || undefined,
        state: selectedStatus.value || undefined,
        startDate: formatDateForAPI(dateRange.value.start),
        endDate: formatDateForAPI(dateRange.value.end)
      }

      // Add ordering parameter if a sort option is selected
      if (selectedSortOption.value) {
        queryParams.ordering = `-${selectedSortOption.value}`
      }

      const response = await CasesService.casesList(queryParams)

      cases.value = response.results.map((caseItem) => ({
        ...caseItem,
        showMoreClerks: false
      })) as CustomerCase[]
      return {
        results: response.results,
        count: response.count,
        next: response.next || null,
        previous: response.previous || null
      }
    } catch (error) {
      console.error('Error fetching cases:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const handleFilterChange = async () => {
    if (viewType.value === 'list') {
      tableRef.value?.refreshData()
    } else {
      // For Kanban view, directly fetch cases
      loading.value = true
      try {
        const response = await fetchCases({
          limit: 100, // Fetch more items for Kanban view
          offset: 0,
          search: searchQuery.value
        })
        // Update the cases array with the response results
        cases.value = response.results.map((caseItem) => ({
          ...caseItem,
          showMoreClerks: false
        })) as CustomerCase[]
      } catch (error) {
        console.error('Error fetching cases for Kanban view:', error)
      } finally {
        loading.value = false
      }
    }
  }

  // Watch all filter changes
  watch(
    [selectedLegalField, selectedCaseWorker, selectedStatus, searchQuery, dateRange],
    () => {
      handleFilterChange()
    },
    { deep: true }
  )

  const formatDateTime = (dateString: string, withTime = true) => {
    const date = new Date(dateString)
    return `${date.toLocaleDateString('de-DE')} ${
      withTime
        ? `um ${date.toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit'
          })} Uhr`
        : ''
    }`
  }

  const getDocumentCountByStatus = (documents: Document[], status: DocumentStatusEnum): number => {
    return documents.filter((doc) => doc.document_status === status).length
  }

  const selectedDocumentStatus = ref<DocumentStatusEnum | null>(null)
  const documentStatuses = [
    DocumentStatusEnum.REQUESTED,
    DocumentStatusEnum.RECEIVED,
    DocumentStatusEnum.COMPLETED
  ]

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'NEW':
        return {
          label: t('newInquiryStatus'),
          class: 'bg-yellow-50 text-yellow-700 border border-yellow-300'
        }
      case 'IN_PROGRESS':
        return {
          label: t('inProgress'),
          class: 'bg-blue-50 text-blue-700 border border-blue-300'
        }
      case 'REJECTED':
        return {
          label: t('rejected'),
          class: 'bg-red-50 text-red-700 border border-red-300'
        }
      default:
        return {
          label: status,
          class: ''
        }
    }
  }

  const handleStatusChange = async (caseItem: any, newStatus: StateEnum) => {
    const previousState = caseItem.state
    caseItem.state = newStatus
    try {
      await CasesService.casesUpdate({
        uuid: caseItem.uuid,
        requestBody: {
          state: newStatus
        }
      })
      // Update was successful, no need to do anything else
    } catch (error) {
      console.error('Failed to update case status:', error)
      // Revert the state back to what it was
      caseItem.state = previousState
      // Show error toast
      genericErrorToast()
    }
  }

  const hasNewActivity = (lastActivity: string, lastActivitySeen: string) => {
    if (!lastActivity || !lastActivitySeen) return false
    return new Date(lastActivity) > new Date(lastActivitySeen)
  }

  const showDeleteModal = ref(false)
  const caseToDelete = ref<any>(null)

  const handleDownloadDocuments = async (caseItem: any) => {
    if (!caseItem?.uuid) return

    try {
      const response = await CasesService.casesDownloadDocumentsZipRetrieve({
        uuid: caseItem.uuid
      })

      // Create a blob from the response and trigger download
      const blob = new Blob([response as unknown as Blob], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `jupus_export_${caseItem.data?.client?.last_name || 'case'}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading documents:', error)
    }
  }

  const handleDelete = (caseItem: any) => {
    caseToDelete.value = caseItem
    showDeleteModal.value = true
  }

  const handleDeleteCase = async () => {
    if (!caseToDelete.value) return

    try {
      await CasesService.casesDestroy({
        uuid: caseToDelete.value.uuid
      })

      tableRef.value?.refreshData()
    } catch (error) {
      console.error('Error deleting case:', error)
    }
  }

  const showFeedbackModal = ref(false)
  const isSwitchBackFeedback = ref(false)

  const handleViewChange = (value: string) => {
    if (value === 'old') {
      // Ask for feedback before switching back
      isSwitchBackFeedback.value = true
      showFeedbackModal.value = true
    } else if (value === 'feedback') {
      // Show regular feedback modal
      isSwitchBackFeedback.value = false
      showFeedbackModal.value = true
    }
    // If 'new' is selected, we're already in the new view, so no action needed
  }

  const handleFeedbackClose = () => {
    showFeedbackModal.value = false
    isSwitchBackFeedback.value = false
  }

  const handleFeedbackSubmitted = (feedbackData: any) => {
    showFeedbackModal.value = false

    // If this was switch back feedback, redirect to old view
    if (isSwitchBackFeedback.value) {
      window.location.href = `/cases/`
      isSwitchBackFeedback.value = false
    } else {
      // Store in localStorage to remember that feedback was given
      localStorage.setItem('newDesignFeedbackGiven', 'true')
    }
  }

  const navigateToCaseDetail = (event: MouseEvent, caseUuid: string) => {
    navigateWithModifiers(event, `/app/cases/${caseUuid}`, (url) => router.push(url))
  }

  const navigateToCaseDetailDocuments = (caseUuid: string) => {
    router.push(`/app/cases/${caseUuid}/documents`)
  }

  // Use the cases directly, sorting is now handled by the backend

  onMounted(() => {
    // fetchFilterOptions()
    if (!user.value) {
      userStore.fetchUser()
    }
  })

  const handleNewCase = () => {
    router.push('/app/cases/create')
  }
</script>

<template>
  <div
    v-if="user"
    class="min-h-screen space-y-8 bg-gray-50 p-4 sm:p-6 md:p-8 lg:p-20 lg:pt-5"
  >
    <div class="mx-auto max-w-7xl">
      <!-- Header with responsive spacing -->
      <div class="mb-4 flex flex-col sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
        <h1 class="mb-3 text-xl font-semibold sm:mb-0 sm:text-2xl">{{ t('title') }}</h1>
        <div class="flex flex-wrap items-center gap-3">
          <!-- Hide view switcher on small screens -->
          <div
            class="hidden h-10 items-center rounded-md border border-gray-200 bg-gray-100 p-1 sm:flex"
          >
            <button
              class="mr-2 flex items-center gap-2 rounded-md px-3 py-1.5 text-sm"
              :class="viewType === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-50'"
              @click="viewType = 'list'"
            >
              <List class="h-4 w-4" />
              <span>Liste</span>
            </button>
            <button
              class="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm"
              :class="viewType === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-50'"
              @click="
                () => {
                  viewType = 'grid'
                  selectedStatus = ''
                  handleFilterChange()
                }
              "
            >
              <Columns3 class="h-4 w-4" />
              <span>Spalten</span>
            </button>
          </div>
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger
                class="flex h-10 items-center gap-2 rounded-md border bg-white px-3 py-2 shadow-sm hover:bg-gray-100"
              >
                <Badge
                  :value="'new'"
                  :title="t('new')"
                  class="h-5 overflow-hidden p-1"
                >
                  <span class="text-xs">{{ t('new') }}</span>
                </Badge>
                <span>{{ t('newView') }}</span>
                <ChevronDown class="h-4 w-4 opacity-50" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                class="min-w-[--radix-dropdown-menu-trigger-width]"
                :align="'end'"
              >
                <DropdownMenuItem
                  class="cursor-pointer"
                  @click="handleViewChange('feedback')"
                >
                  <MessageCircleMore class="mr-2 h-4 w-4" />
                  <span>{{ t('giveFeedback') }}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  class="cursor-pointer"
                  @click="handleViewChange('old')"
                >
                  <Undo2 class="mr-2 h-4 w-4" />
                  <span>{{ t('backToOldView') }}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Button
            variant="default"
            data-cy="create-case-button"
            class="flex w-full items-center gap-2 sm:w-auto"
            @click="handleNewCase"
          >
            <Plus class="h-4 w-4 text-white" />
            <span class="text-white">Neue Anfrage anlegen</span>
          </Button>
        </div>
      </div>

      <div class="mx-auto max-w-7xl rounded-xl bg-white shadow-sm ring-1 ring-gray-900/5">
        <!-- Table with horizontal scroll on small screens -->
        <PaginatedTableView
          v-if="viewType === 'list'"
          ref="tableRef"
          data-cy="cases-list"
          :fetch-data="fetchCases"
          :loading="loading"
          :searchable-fields="t('name')"
          class="overflow-hidden rounded-xl border-none bg-white p-4 lg:p-6"
        >
          <!-- Responsive filters -->
          <template #actions>
            <div
              class="flex flex-col flex-wrap items-start gap-3 p-2 sm:flex-row sm:items-center sm:p-3"
            >
              <div class="w-full sm:w-auto">
                <SelectField
                  v-model="selectedLegalField"
                  :options="lawFieldOptions"
                  :placeholder="t('legalField')"
                  :allow-unselect="true"
                  :unselected-option-text="t('allLegalFields')"
                  class="w-full sm:w-44"
                  name="legalfield"
                  @update:model-value="handleFilterChange"
                />
              </div>
              <div class="w-full sm:w-auto">
                <SelectField
                  v-model="selectedCaseWorker"
                  :options="caseWorkerOptions"
                  :placeholder="t('caseWorker')"
                  :allow-unselect="true"
                  :unselected-option-text="t('allCaseWorkers')"
                  class="w-full sm:w-44"
                  name="caseWorker"
                  @update:model-value="handleFilterChange"
                />
              </div>
              <div class="w-full sm:w-auto">
                <SelectField
                  v-model="selectedStatus"
                  :options="statusOptions"
                  :placeholder="t('status')"
                  :allow-unselect="true"
                  :unselected-option-text="t('allStatuses')"
                  class="w-full sm:w-44"
                  name="status"
                  @update:model-value="handleFilterChange"
                >
                  <template #value>
                    <Badge
                      v-if="selectedStatus"
                      class="ml-2"
                      :class="getStatusBadge(selectedStatus).class"
                    >
                      3
                    </Badge>
                  </template>
                </SelectField>
              </div>
              <div class="w-full sm:w-auto">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="secondary"
                      class="w-full gap-2"
                    >
                      <Calendar class="h-4 w-4" />
                      {{ dateRangeText }}
                      <X
                        v-if="dateRange.start || dateRange.end"
                        class="h-4 w-4 rounded-sm hover:bg-gray-400"
                        @click.stop="dateRange = { start: undefined, end: undefined }"
                      />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <RangeCalendar
                      v-model="dateRange"
                      @update:model-value="handleFilterChange"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div class="w-full sm:w-auto">
                <SelectField
                  v-model="selectedSortOption"
                  :options="sortOptions"
                  class="w-full sm:w-44"
                  name="sortOption"
                  @update:model-value="handleFilterChange"
                />
              </div>
            </div>
          </template>

          <template #table-head>
            <!-- Hide activity status column on mobile -->
            <th class="hidden py-3.5 pl-4 pr-3 sm:table-cell">
              <span class="sr-only">Activity Status</span>
            </th>
            <th class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
              {{ t('name') }}
            </th>
            <!-- Hide these columns on mobile -->
            <th
              class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            >
              {{ t('legalFieldTheme') }}
            </th>
            <th
              class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 md:table-cell"
            >
              {{ t('caseWorker') }}
            </th>
            <th class="w-1/4 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:w-auto">
              {{ t('status') }}
            </th>
            <th
              class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 md:table-cell"
            >
              {{ t('updatedAt') }}
            </th>
            <th
              class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 md:table-cell"
            >
              {{ t('documents') }}
            </th>
            <th
              class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 md:table-cell"
            >
              {{ t('notes') }}
            </th>
            <th class="relative w-12 px-3 py-3.5">
              <span class="sr-only">Actions</span>
            </th>
          </template>

          <template #table-row>
            <TableRow
              v-for="caseItem in cases"
              :key="caseItem.uuid"
              data-cy="case-row"
              class="group cursor-pointer hover:bg-gray-50"
              @click="navigateToCaseDetail($event, caseItem.uuid as string)"
              @auxclick="navigateToCaseDetail($event, caseItem.uuid as string)"
            >
              <!-- Activity indicator - hidden on mobile -->
              <TableCell class="hidden py-2 pl-2 pr-1 sm:table-cell sm:py-4 sm:pl-4 sm:pr-3">
                <div
                  v-if="
                    hasNewActivity(
                      caseItem.last_activity_at as string,
                      caseItem.last_activity_seen_at as string
                    )
                  "
                  class="relative flex h-2 w-2"
                  :title="formatDateTime(caseItem.last_activity_at as string)"
                >
                  <span
                    class="absolute inline-flex h-full w-full animate-ping rounded-full bg-purple-400 opacity-75"
                  ></span>
                  <span
                    class="relative inline-flex h-2 w-2 rounded-full bg-purple-500 shadow-[0_0_10px_2px_rgba(139,70,255,0.3)]"
                  ></span>
                </div>
              </TableCell>

              <!-- Responsive name cell with mobile-only extra info -->
              <TableCell class="py-2 pl-2 pr-1 sm:py-4 sm:pl-4 sm:pr-3">
                <div class="flex items-start">
                  <!-- Activity indicator for mobile only -->
                  <div class="mr-2 mt-1.5 sm:hidden">
                    <div
                      v-if="
                        hasNewActivity(
                          caseItem.last_activity_at as string,
                          caseItem.last_activity_seen_at as string
                        )
                      "
                      class="relative flex h-2 w-2"
                      :title="formatDateTime(caseItem.last_activity_at as string)"
                    >
                      <span
                        class="absolute inline-flex h-full w-full animate-ping rounded-full bg-purple-400 opacity-75"
                      ></span>
                      <span
                        class="relative inline-flex h-2 w-2 rounded-full bg-purple-500 shadow-[0_0_10px_2px_rgba(139,70,255,0.3)]"
                      ></span>
                    </div>
                  </div>
                  <div>
                    <div class="font-medium text-gray-900">
                      {{ caseItem.data?.client.first_name + ' ' + caseItem.data?.client.last_name }}
                    </div>
                    <!-- Mobile-only info -->
                    <div class="mt-1 flex flex-col gap-1 sm:hidden">
                      <div
                        v-if="caseItem.data?.law.field || caseItem.data?.law.topic"
                        class="text-sm text-gray-500"
                      >
                        {{ translateLawField(caseItem.data.law.field)
                        }}{{ caseItem.data.law.field && caseItem.data.law.topic ? '/' : ''
                        }}{{ translateLawTopic(caseItem.data.law.topic) }}
                      </div>
                      <div
                        v-if="caseItem.clerk"
                        class="text-sm text-gray-500"
                      >
                        {{ caseItem.clerk.first_name + ' ' + caseItem.clerk.last_name }}
                      </div>
                      <div class="text-sm text-gray-500">
                        {{ formatDateTime(caseItem.created_at || '') }}
                      </div>
                    </div>
                  </div>
                </div>
              </TableCell>

              <!-- Desktop-only columns -->
              <TableCell class="hidden px-3 py-4 sm:table-cell">
                <div class="flex flex-col">
                  <template v-if="caseItem.data?.law?.field || caseItem.data?.law?.topic">
                    <span>{{ caseItem.data?.law?.field && caseItem.data?.law?.field + '/' }}</span>
                    <span>{{ caseItem.data?.law?.topic || '' }}</span>
                  </template>
                  <span v-else>-</span>
                </div>
              </TableCell>

              <TableCell class="hidden px-3 py-4 md:table-cell">
                <div
                  v-if="caseItem.clerk"
                  class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1"
                >
                  <div
                    class="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500 text-sm text-white"
                  >
                    {{ caseItem.clerk.first_name.charAt(0) }}
                  </div>
                  <span class="text-sm">
                    {{ caseItem.clerk.first_name + ' ' + caseItem.clerk.last_name }}
                  </span>
                </div>
                <span
                  v-else
                  class="text-gray-500"
                >
                  {{ t('notAssigned') }}
                </span>
              </TableCell>

              <!-- Status appears on all screen sizes -->
              <TableCell class="px-1 py-2 sm:px-3 sm:py-4">
                <CaseStatusSelect
                  :id="`case-status-${caseItem.id}`"
                  :model-value="caseItem.state"
                  :compact="true"
                  class="w-full"
                  @update:model-value="(newStatus) => handleStatusChange(caseItem, newStatus)"
                />
              </TableCell>

              <TableCell class="hidden px-3 py-4 md:table-cell">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <div class="flex items-center truncate text-sm text-gray-500">
                        <Clock4 class="mr-2 size-4 min-w-4" />
                        {{ formatDateTime(caseItem.modified_at, false) }}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      variant="secondary"
                      showArrow
                    >
                      <div class="flex flex-col gap-1 text-sm">
                        <div class="flex items-center">
                          <strong>{{ t('updated') }}:</strong>
                          <span class="ml-1">{{ formatDateTime(caseItem.modified_at) }}</span>
                        </div>
                        <div class="flex items-center">
                          <strong>{{ t('created') }}:</strong>
                          <span class="ml-1">{{ formatDateTime(caseItem.created_at || '') }}</span>
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
              <TableCell class="hidden text-nowrap px-3 py-4 text-gray-500 md:table-cell">
                <Popover v-if="caseItem.documents?.length">
                  <PopoverTrigger @click.stop>
                    <div class="flex cursor-pointer items-center gap-1">
                      <template
                        v-for="status in documentStatuses"
                        :key="status"
                      >
                        <DocumentStatusBadge
                          v-if="getDocumentCountByStatus(caseItem.documents, status) > 0"
                          :type="status"
                          :count="getDocumentCountByStatus(caseItem.documents, status)"
                          @click="selectedDocumentStatus = status"
                        />
                      </template>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent
                    class="p-4"
                    :align="'start'"
                  >
                    <div class="flex flex-col items-start space-y-3">
                      <div class="flex w-full items-center justify-between">
                        <div
                          v-if="selectedDocumentStatus"
                          class="flex items-center gap-2"
                        >
                          <span class="text-sm font-medium">
                            {{
                              getDocumentCountByStatus(caseItem.documents, selectedDocumentStatus)
                            }}
                            {{
                              getDocumentCountByStatus(
                                caseItem.documents,
                                selectedDocumentStatus
                              ) === 1
                                ? t('document')
                                : t('documents')
                            }}
                          </span>
                          <DocumentStatusBadge
                            :type="selectedDocumentStatus"
                            showText
                          />
                        </div>
                        <X
                          class="size-5 cursor-pointer text-gray-500 hover:text-gray-700"
                          @click="selectedDocumentStatus = null"
                        />
                      </div>
                      <a
                        href="#"
                        class="mt-2 text-center text-purple-600 underline hover:text-purple-800"
                        @click.stop="navigateToCaseDetailDocuments(caseItem.uuid || '')"
                      >
                        {{ t('viewAllDocuments') }}
                      </a>
                    </div>
                  </PopoverContent>
                </Popover>
                <span v-else>
                  {{ t('noDocuments') }}
                </span>
              </TableCell>
              <TableCell class="hidden px-3 py-4 md:table-cell">
                <Popover>
                  <PopoverTrigger
                    v-if="caseItem.note"
                    @click.stop
                  >
                    <Badge
                      variant="secondary"
                      class="flex items-center gap-1 rounded-md p-1 font-medium text-gray-500"
                      :class="caseItem.note ? 'hover:brightness-95' : ''"
                    >
                      <StickyNote class="size-4" />
                      {{ '1' }}
                    </Badge>
                  </PopoverTrigger>
                  <PopoverContent v-if="caseItem.note">
                    <div
                      v-if="caseItem.clerk"
                      class="flex items-center gap-2 pb-2"
                    >
                      <UserAvatar
                        :user="caseItem.clerk"
                        size="32px"
                      />
                      <div class="flex flex-col">
                        <span class="text-md font-medium">
                          {{ caseItem.clerk.first_name }} {{ caseItem.clerk.last_name }}
                        </span>
                        <span class="text-sm text-gray-500">{{ caseItem.clerk.email }}</span>
                      </div>
                    </div>
                    <div class="flex flex-col gap-1 text-sm">
                      {{ caseItem.note }}
                    </div>
                  </PopoverContent>
                </Popover>
              </TableCell>

              <TableCell
                class="px-2 py-2 text-right sm:px-3 sm:py-4"
                @click.stop
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                    >
                      <MoreVertical class="h-4 w-4" />
                      <span class="sr-only">Open menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent :align="'end'">
                    <DropdownMenuItem @click="navigateToCaseDetail($event, caseItem.uuid || '')">
                      <Eye class="mr-2 size-4" />
                      {{ t('view') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem @click="handleDownloadDocuments(caseItem)">
                      <FileDown class="mr-2 size-4" />
                      Alle Dokumente herunterladen
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      class="text-red-600 hover:bg-red-50"
                      @click="handleDelete(caseItem)"
                    >
                      <Trash2 class="mr-2 size-4" />
                      Anfrage löschen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          </template>
        </PaginatedTableView>

        <!-- Kanban/Grid View -->
        <div
          v-else
          class="p-4"
        >
          <!-- Filters -->
          <div class="flex flex-wrap items-center gap-4 p-4">
            <div class="relative w-full sm:w-auto sm:min-w-[290px] sm:flex-1">
              <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search class="h-4 w-4 text-gray-400" />
              </div>
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="t('name')"
                class="block w-full rounded-md border border-gray-300 bg-gray-50 py-1.5 pl-10 pr-3 text-gray-900
                  focus:border-purple-600 focus:ring-2 focus:ring-purple-600 sm:text-sm sm:leading-6"
                @input="handleFilterChange"
              />
            </div>
            <div class="flex flex-wrap items-center gap-3">
              <SelectField
                v-model="selectedLegalField"
                :options="lawFieldOptions"
                :placeholder="t('legalField')"
                :allow-unselect="true"
                :unselected-option-text="t('allLegalFields')"
                class="w-full sm:w-44"
                name="legalfield"
                @update:model-value="handleFilterChange"
              />
              <SelectField
                v-model="selectedCaseWorker"
                :options="caseWorkerOptions"
                :placeholder="t('caseWorker')"
                :allow-unselect="true"
                :unselected-option-text="t('allCaseWorkers')"
                class="w-full sm:w-44"
                name="caseWorker"
                @update:model-value="handleFilterChange"
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="secondary"
                    class="w-full gap-2 sm:w-auto"
                  >
                    <Calendar class="h-4 w-4" />
                    {{ dateRangeText }}
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <RangeCalendar
                    v-model="dateRange"
                    @update:model-value="handleFilterChange"
                  />
                </PopoverContent>
              </Popover>
              <SelectField
                v-model="selectedSortOption"
                :options="sortOptions"
                class="w-full sm:w-44"
                name="sortOption"
                @update:model-value="handleFilterChange"
              />
            </div>
          </div>

          <!-- Kanban Board -->
          <KanbanView
            :items="cases"
            :loading="loading"
            :t="t"
            :on-status-change="handleStatusChange"
          />
        </div>
      </div>
    </div>
    <AlertDialog v-model:open="showDeleteModal">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Sind Sie sicher?</AlertDialogTitle>
          <AlertDialogDescription>
            Diese Aktion kann nicht rückgängig gemacht werden. Die Anfrage und alle zugehörigen
            Daten werden dauerhaft gelöscht.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel @click="showDeleteModal = false">Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            class="bg-red-600 hover:bg-red-700"
            @click="handleDeleteCase"
          >
            Löschen
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>

  <NewPageBanner mode="case-overview" />
  <!-- Feedback Modal -->
  <FeedbackModal
    v-if="showFeedbackModal"
    :title="
      isSwitchBackFeedback
        ? 'Warum kehren Sie zum alten Design zurück?'
        : 'Was könnten wir am neuen Design verbessern?'
    "
    description="Bitte teilen Sie uns Ihr Feedback mit. Dies hilft uns sicherzustellen, dass das neue Design allen Ihren Anforderungen entspricht."
    :feature="FeatureEnum.NEW_CASE_PAGE"
    :showThumbsRating="false"
    @close="handleFeedbackClose"
    @submitted="handleFeedbackSubmitted"
  />
</template>

<style scoped>
  .kanban-column {
    min-height: 500px;
  }

  /* Add smooth transition for dragging */
  .sortable-ghost {
    opacity: 0.5;
    background: #f7f7f7;
  }

  .sortable-drag {
    opacity: 0.9;
    transform: rotate(2deg);
    box-shadow:
      0 4px 6px -1px rgb(0 0 0 / 0.1),
      0 2px 4px -2px rgb(0 0 0 / 0.1);
  }
</style>
